/**
 * Product Recommendations
 * Supports both Shopify and Malachyte recommendations
 */
import { initLazyImages } from './lazyImages.js';
import { malachyteAPI } from './malachyte-api.js';

class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: "0px 0px 400px 0px" }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== "attributes") continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === "data-recommendations-performed" &&
        this.dataset.recommendationsPerformed === "true"
      )
        continue;

      // Ignore error attribute changes to prevent infinite loop
      if (mutation.attributeName === "data-error") continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent, useMalachyte } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error("Product ID and an ID attribute are required");
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === "true") {
      return;
    }

    // Use Malachyte recommendations if enabled
    if (useMalachyte === "true") {
      this.#loadMalachyteRecommendations();
      return;
    }

    // Otherwise use Shopify recommendations
    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then(result => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement("div");
        html.innerHTML = result.data || "";
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = "true";
          this.innerHTML = recommendations.innerHTML;

          // Initialize lazy images for the newly inserted product cards
          initLazyImages();

          // Initialize Carousel
          this.#initCarousel();
        } else {
          this.#handleError(new Error("No recommendations available"));
        }
      })
      .catch(e => {
        this.#handleError(e);
      });
  }

  /**
   * Initialize the carousel logic
   */
  #initCarousel() {
    const carousels = this.querySelectorAll(".swiper__carousel");
    if (!carousels.length) return;

    carousels.forEach(carousel => {
      // Read section ID from data attribute
      const sectionId = carousel.dataset.sectionId || this.dataset.sectionId;

      // // Determine layout/mode
      // // If it's the mobile-only carousel (hidden--desktop), we behave differently
      // const isMobileInstance = carousel.closest(".hidden--desktop") !== null;

      // // Get gap from CSS variable on the wrapper or computed style
      // // Wrapper: product-recommendations__carousel-wrapper
      // // CSS Var: --product-recommendations-column-gap
      // let columnsGap = 16; // default
      // const wrapper = carousel.closest(".product-recommendations__carousel-wrapper");
      // if (wrapper) {
      //   const gapVal = getComputedStyle(wrapper).getPropertyValue("--product-recommendations-column-gap");
      //   if (gapVal) columnsGap = parseFloat(gapVal);
      // }

      const config = {
        slidesPerView: 1.3,
        spaceBetween: 16,
        speed: 600,
        loop: false,
        breakpoints: {
          320: {
            enabled: true,
            slidesPerView: 1.3,
            spaceBetween: 16,
            slidesOffsetAfter: 28,
          },
          768: {
            enabled: true,
            spaceBetween: 20,
            slidesOffsetAfter: 32,
          },
          1280: {
            enabled: false,
          },
        },
        navigation: {
          nextEl: `[data-section-id="${sectionId}"] .product-recommendations__nav-next`,
          prevEl: `[data-section-id="${sectionId}"] .product-recommendations__nav-prev`,
        },
        pagination: {
          el: `[data-section-id="${sectionId}"] .product-recommendations__pagination`,
          type: "bullets",
          bulletClass: "product-recommendations__pagination-bullet h-2 w-4 bg-medium-asphalt rounded-full",
          bulletActiveClass: "product-recommendations__pagination-bullet-active w-9 bg-brunt-orange!",
          clickable: true,
        },
        a11y: {
          enabled: true,
          prevSlideMessage: "Previous slide",
          nextSlideMessage: "Next slide",
          firstSlideMessage: "This is the first slide",
          lastSlideMessage: "This is the last slide",
          paginationBulletMessage: "Go to slide",
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
      };

      if (window.initCarousel) {
        window.initCarousel(carousel, config);
      }
    });
  }

  /**
   * Load Malachyte recommendations
   */
  async #loadMalachyteRecommendations() {
    const { productId, sectionId, malachyteStrategy, maxProducts } = this.dataset;
    const id = this.id;

    try {
      // Fetch recommendations from Malachyte
      const recommendations = await malachyteAPI.getRecommendations(productId, {
        k: parseInt(maxProducts, 10) || 4,
        strategyName: malachyteStrategy || "recommended-products",
      });

      if (!recommendations || recommendations.length === 0) {
        console.error('[Malachyte] No recommendations returned from API');
        this.#handleError(new Error("No Malachyte recommendations available"));
        return;
      }

      // Extract product IDs from Malachyte response
      // Malachyte returns product IDs nested inside facts object
      const productIds = recommendations
        .map(product => product.facts?.id || product.id || product.handle || product.product_id)
        .filter(Boolean);

      if (!productIds || productIds.length === 0) {
        console.error('[Malachyte] No valid product IDs in Malachyte response', recommendations);
        this.#handleError(new Error("No valid product IDs in Malachyte response"));
        return;
      }

      // Fetch product handles from GraphQL
      const productHandles = await this.#fetchProductHandlesByIds(productIds);

      if (!productHandles || productHandles.length === 0) {
        console.error('[Malachyte] No valid product handles returned from GraphQL', productIds);
        this.#handleError(new Error("Could not fetch product handles"));
        return;
      }

      // Build URL with product handles in the path (not query params)
      // Liquid can access request.path but not query params in Section Rendering API
      const productHandlesString = productHandles.join(',');
      const url = `${window.location.pathname}/${productHandlesString}?section_id=${sectionId}`;
      
      this.#activeFetch?.abort();
      this.#activeFetch = new AbortController();

      const response = await fetch(url, { signal: this.#activeFetch.signal });
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const text = await response.text();
      
      this.#renderMalachyteRecommendations(text, id);
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was cancelled, ignore
        return;
      }
      this.#handleError(error);
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Fetch product handles from Shopify GraphQL by product IDs
   * @param {Array<string>} productIds - Array of product IDs
   * @returns {Promise<Array<string>>} Array of product handles
   */
  async #fetchProductHandlesByIds(productIds) {
    try {
      // Convert numeric IDs to Shopify GID format
      const gids = productIds.map(id => `gid://shopify/Product/${id}`);
      
      // Fetch just the handles - we'll let Liquid render the products
      const query = `
        query($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on Product {
              handle
            }
          }
        }
      `;

      const variables = {
        ids: gids
      };

      if (!window.Shopify?.storefrontAccessToken) {
        throw new Error('Storefront API token not configured. Please add it in Theme Settings > API Configuration.');
      }

      if (!window.Shopify?.shop) {
        throw new Error('Shopify shop domain not available.');
      }

      // Use Shopify Storefront API endpoint - must use full HTTPS URL to bypass local dev server
      const shopDomain = window.Shopify.shop;
      // Use current API version 2025-01
      const apiUrl = `https://${shopDomain}/api/2025-01/graphql.json`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.Shopify.storefrontAccessToken,
        },
        body: JSON.stringify({ 
          query,
          variables 
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Malachyte] GraphQL error response:', errorText);
        throw new Error(`GraphQL request failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();

      if (result.errors) {
        console.error('[Malachyte] GraphQL errors:', result.errors);
        throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
      }

      // Extract product handles from nodes response
      const handles = [];
      if (result.data?.nodes) {
        for (const node of result.data.nodes) {
          if (node?.handle) {
            handles.push(node.handle);
          }
        }
      }

      return handles;
    } catch (error) {
      console.error('[Malachyte] Error fetching product handles:', error);
      throw error;
    }
  }

  /**
   * Render Malachyte recommendations from HTML response
   * @param {string} htmlText - HTML response text
   * @param {string} id - Element ID
   */
  #renderMalachyteRecommendations(htmlText, id) {
    const html = document.createElement("div");
    html.innerHTML = htmlText;
    const recommendationsElement = html.querySelector(`product-recommendations[id="${id}"]`);
      
    if (recommendationsElement?.innerHTML && recommendationsElement.innerHTML.trim().length) {
      this.dataset.recommendationsPerformed = "true";
      this.innerHTML = recommendationsElement.innerHTML;

      // Initialize lazy images for the newly inserted product cards
      initLazyImages();

      // Initialize Carousel
      this.#initCarousel();
    } else {
      this.#handleError(new Error("No recommendations available"));
    }
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error("Product recommendations error:", error);
    console.error("Error stack:", error.stack);
    
    // Mark as performed to prevent infinite loops
    this.dataset.recommendationsPerformed = "true";
    
    // Set error state (mutation observer will ignore this)
    this.dataset.error = error.message || "Error loading product recommendations";
    
    // Hide the section
    this.classList.add("hidden");
  }
}

if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}
